import Button from '@/shared/Button'
import FormCreator from '@/shared/FormCreator'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'

import { useEffect, useRef, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import * as Yup from 'yup'

import DialogHeader from '@/shared/DialogHeader'
import Typography from '@mui/material/Typography'
import moment from 'moment'


const mapStateToProps = ({ loading }) => ({
  isLoading: loading['company/createCompany'],
  isPlanLoading: loading['plan/getPlanLevel'],
})

const inputConfig = {
  inputBaseProps: {
    labelSuffix: '', // 不加冒號或其他符號
    justifyContent: 'center', // 將 label 與 input 靠上對齊
    direction: 'column', // label 在上，input 在下
    alignItems: 'stretch', // input 撐滿整個欄位
    labelProps: {
      sx: {
        mb: -2, // 調小 label 與 input 的距離
        lineHeight: 1.2, // 行高
        fontWeight: 400, // 適中，不要太輕
        fontSize: 12, // 確保字體大小統一
      },
    },
    childrenSx: {
      width: '95%',
      mt: 0, // 去掉 input 上方的多餘間距
    },
  },
  color: 'btn_positive',
  endAdornment: args => {
    const value = args?.value || ''
    const maxLength = args?.inputProps?.maxLength || 0

    if (!maxLength) return null

    return (
      <Typography
        variant="caption"
        sx={{ color: '#00000040', fontSize: '12px !important' }}
      >{`${value.length}/${maxLength}`}</Typography>
    )
  },
}

const CreateCompanyDialog = ({
  dispatch,
  isLoading,
  isPlanLoading,
  onClose,
  onComplete,
  ...props
}) => {
  const form = useRef()
  const { t } = useTranslation()
  const [planResults, setPlanResults] = useState([])
  const [planMap, setPlanMap] = useState({})
  // const [filterDateRange, setFilterDateRange] = useState()

  const handleClose = () => {
    const { reset } = form.current
    reset?.()
    onClose?.()
  }



  const getPlanList = () => {
    if (!isPlanLoading) {
      dispatch({
        type: 'plan/getPlanLevel',
        successCallback: response => {
          const responseData = response?.data || []

          const planOptions = responseData.map(plan => ({
            value: plan.id,
            label: plan.name,
          }))

          const map = {}
          responseData.forEach(plan => {
            map[plan.id] = plan
          })

          setPlanResults(planOptions)
          setPlanMap(map)

          const defaultPlan = map[0]

          if (defaultPlan && form.current) {
            form.current.setValue?.('emailCount', defaultPlan.emailCount)
            form.current.setValue?.('preEmailCount', defaultPlan.preEmailCount)
          }
        }
      })
    }
  }

  const defaultRange = useMemo(() => ({
    startDate: new Date(),
    endDate: new Date(),
  }), [])

  const [filterDateRange, setFilterDateRange] = useState(defaultRange)

  const handleSubmit = () => {
    if (filterDateRange) {
      const { handleSubmit: formSubmit } = form.current
      formSubmit?.(values => {
        const { companyName, companyAccountCount, planId, emailCount, preEmailCount, status } = values

        dispatch({
          type: 'company/createCompany',
          payload: {
            companyName,
            companyAccountCount,
            planId,
            emailCount,
            preEmailCount,
            planStartTime: moment(filterDateRange.startDate).toDate(),
            planEndTime: moment(filterDateRange.endDate).toDate(),
            status,
          },
          successCallback: () => {
            dispatch({
              type: 'app/notify',
              payload: { message: t('messages.company_created'), severity: 'success' },
            })
            onClose?.()
            onComplete?.()
          },
          errorCallback: () => {
            dispatch({
              type: 'app/notify',
              payload: {
                message: t('messages.company_create_failed'),
                severity: 'error',
              },
            });
          },
        })
      })?.()
    }
  }

  useEffect(() => {
    setFilterDateRange(defaultRange)
  }, [defaultRange])

  useEffect(() => {
    if (props.open) {
      getPlanList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open])

  return (
    <Dialog {...props} maxWidth={'xs'}
      PaperProps={{
        sx: {
          borderRadius: 5, // ← 圓角在這裡才有效
          overflow: 'hidden', //  必加，不然 Header 會蓋掉圓角
        },
      }}>

      <DialogHeader
        title={t('general.add_logo')}
        onClose={handleClose}
      />

      <DialogContent sx={{ width: 450, pr: 1 }}>
        <FormCreator
          ref={form}
          gridItemProps={{ md: 12 }}
          formConfig={{ mode: 'submit' }}
          formItems={[
            {
              label: t('general.company_name'),
              name: 'companyName',
              defaultValue: '',
              validator: Yup.mixed()
                .test(
                  'required',
                  t('validation.company_required'),
                  value => value !== null && value !== undefined && value !== ''
                ),
              inputConfig: {
                ...inputConfig,
                inputProps: { maxLength: 40 }
              },
            },
            {
              label: t('general.plan'),
              name: 'planId',
              type: 'select',
              defaultValue: '6',
              inputConfig: {
                ...inputConfig,
                options: planResults,
                onChange: e => {
                  const selectedValue = Number(
                    e?.target?.value ?? e ?? 0
                  )
                  const selectedPlan = planMap[selectedValue]

                  if (selectedPlan && form.current) {
                    form.current.setValue?.('emailCount', selectedPlan.emailCount)
                    form.current.setValue?.('preEmailCount', selectedPlan.preEmailCount)
                  }
                }
              },
            },
            {
              label: t('general.available_account_count'),
              name: 'companyAccountCount',
              type: 'select',
              defaultValue: '1',
              inputConfig: {
                ...inputConfig,
                options: [
                  { label: '1', value: '1' },
                  { label: '2', value: '2' },
                  { label: '3', value: '3' },
                  { label: '4', value: '4' },
                  { label: '5', value: '5' },
                  { label: '6', value: '6' },
                  { label: '7', value: '7' },
                  { label: '8', value: '8' },
                  { label: '9', value: '9' },
                  { label: '10', value: '10' },
                ],
              },
            },
            {
              label: t('general.authorization_duration'),
              type: 'labelOnly',
            },
            {
              name: 'dateRangeFilter',
              type: 'dateRange',
              defaultValue: defaultRange,
              inputConfig: {
                onChange: value => {
                  setFilterDateRange(value)
                },
              },
            },
            {
              label: t('general.status'),
              name: 'status',
              type: 'select',
              defaultValue: 'A',
              inputConfig: {
                ...inputConfig,
                options: [
                  { label: t('status.ACTIVE'), value: 'A' },
                  { label: t('status.INACTIVE'), value: 'IA' },
                ],
              },
            },
          ].filter(Boolean)}
          submitComponent={() => null}
          resetComponent={() => null}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: 'flex-end', //  右下角
        }}>
        <Button
          style={{ minWidth: 100, backgroundColor: '#2D2D6C' }}
          loading={isLoading}
          disabled={isLoading}
          onClick={async () => {
            handleSubmit()
          }}
        >
          {t('general.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(CreateCompanyDialog)